import { useCallback, useEffect, useMemo, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { createPortal } from "react-dom";
import { motion, AnimatePresence } from "framer-motion";
import {
  StickyNote, X, Send, Loader2, Trash2, Pencil, Check,
  CheckCircle2, Circle, Globe, MessageSquare, Pin, PinOff, ExternalLink,
} from "lucide-react";
import { toast } from "sonner";
import { api, formatApiErrorDetail } from "@/lib/api";
import { UserInitialsChip } from "@/components/UserInitialsChip";
import { RichNoteEditor } from "@/components/RichNoteEditor";
import { NoteBody, NoteAttachments } from "@/components/NoteContent";
import { AssignControl } from "@/components/AssignControl";
import { DueDateControl } from "@/components/DueDateControl";
import { recordLink, ENTITY_MODULE, notifyNotesChanged } from "@/lib/notesRouting";
import { Switch } from "@/components/ui/switch";

// Route → { module key, label }. Keys match the RBAC module keys / general pages.
function moduleForPath(pathname) {
  const p = pathname || "/";
  if (p === "/") return { key: "dashboard", label: "Dashboard" };
  if (p.startsWith("/activity")) return { key: "activity", label: "Activity" };
  if (p.startsWith("/asset-library")) return { key: "assetlibrary", label: "Asset Library" };
  if (p.startsWith("/products")) return { key: "products", label: "Marketing Products" };
  if (p.startsWith("/suppliers-quotes")) return { key: "suppliersquotes", label: "Suppliers & Quotes" };
  if (p.startsWith("/requests")) return { key: "requests", label: "Marketing Requests" };
  if (p.startsWith("/seo-meta")) return { key: "seo_meta", label: "SEO Meta Editor" };
  if (p.startsWith("/seo")) return { key: "seo", label: "On-Page SEO" };
  if (p.startsWith("/faq")) return { key: "faq", label: "FAQs Creator" };
  if (p.startsWith("/theme-design")) return { key: "theme_design", label: "Theme Design" };
  if (p.startsWith("/help")) return { key: "help", label: "Help Center" };
  if (p.startsWith("/orchestrator")) return { key: "orchestrator", label: "Orchestrator" };
  if (p.startsWith("/settings")) return { key: "settings", label: "Settings" };
  return { key: "dashboard", label: "Dashboard" };
}

const htmlToText = (html) => { const d = new DOMParser().parseFromString(html || "", "text/html"); return (d.body.textContent || "").trim(); };
function timeLabel(iso) {
  if (!iso) return "";
  return new Date(iso).toLocaleString(undefined, {
    day: "numeric", month: "short", year: "numeric", hour: "2-digit", minute: "2-digit",
  });
}

export function GlobalNotes() {
  const location = useLocation();
  const navigate = useNavigate();
  const ctx = useMemo(() => moduleForPath(location.pathname), [location.pathname]);

  const [open, setOpen] = useState(false);
  const [showAll, setShowAll] = useState(false);
  const [showResolved, setShowResolved] = useState(false);
  const [notes, setNotes] = useState([]);
  const [loading, setLoading] = useState(false);
  const [text, setText] = useState("");
  const [html, setHtml] = useState("");
  const [attachments, setAttachments] = useState([]);
  const [mentions, setMentions] = useState([]);
  const [submitting, setSubmitting] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [editText, setEditText] = useState("");
  const [editHtml, setEditHtml] = useState("");
  const [editMentions, setEditMentions] = useState([]);
  const [editAttachments, setEditAttachments] = useState([]);
  const [counts, setCounts] = useState({ module_open: 0, all_open: 0 });
  // Entity-focus mode: when set, the sidebar shows ONLY one record's notes.
  const [focus, setFocus] = useState(null); // { entityType, entityId, label }

  // Open the panel from anywhere. With a detail payload, focus a single record.
  useEffect(() => {
    const openIt = (e) => {
      const d = e?.detail || {};
      if (d.entityType && d.entityId) {
        setFocus({ entityType: d.entityType, entityId: d.entityId, label: d.label || "" });
      } else {
        setFocus(null);
      }
      setText(""); setMentions([]); setEditingId(null);
      setOpen(true);
    };
    window.addEventListener("mh:open-notes", openIt);
    return () => window.removeEventListener("mh:open-notes", openIt);
  }, []);

  const composerModule = focus ? ENTITY_MODULE[focus.entityType] : ctx.key;

  const refreshCounts = useCallback(async () => {
    try {
      const { data } = await api.get("/notes/module/counts", { params: { module: ctx.key } });
      setCounts({ module_open: data.module_open || 0, all_open: data.all_open || 0 });
    } catch { /* silent — badge only */ }
  }, [ctx.key]);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      if (focus) {
        const { data } = await api.get("/notes", {
          params: { entity_type: focus.entityType, entity_id: focus.entityId },
        });
        setNotes(data.notes || []);
      } else {
        const { data } = await api.get("/notes/module", {
          params: { module: ctx.key, show_all: showAll, show_resolved: showResolved },
        });
        setNotes(data.notes || []);
      }
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Could not load notes");
    } finally {
      setLoading(false);
    }
  }, [focus, ctx.key, showAll, showResolved]);

  // Badge refresh on mount + whenever the page/module changes.
  useEffect(() => { refreshCounts(); }, [refreshCounts]);
  // Reload the list whenever the panel is open and filters/context change.
  useEffect(() => { if (open) load(); }, [open, load]);

  const submit = async () => {
    const content = htmlToText(html);
    if (!content && !attachments.length) return;
    setSubmitting(true);
    const payloadExtra = { content: content || "(attachment)", content_html: html, attachments, mentions: mentions.map((m) => m.user_id) };
    try {
      if (focus) {
        const { data } = await api.post("/notes", {
          entity_type: focus.entityType, entity_id: focus.entityId, entity_label: focus.label,
          ...payloadExtra,
        });
        setNotes((prev) => [data, ...prev]);
        notifyNotesChanged(focus.entityType, focus.entityId);
      } else {
        const { data } = await api.post("/notes/module", { module: ctx.key, ...payloadExtra });
        const visible = (showAll || data.module === ctx.key) && (showResolved || !data.resolved);
        if (visible) setNotes((prev) => [data, ...prev]);
        refreshCounts();
      }
      setText(""); setHtml(""); setAttachments([]); setMentions([]);
      toast.success(mentions.length ? "Note added — teammates notified" : "Note added");
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Could not add note");
    } finally {
      setSubmitting(false);
    }
  };

  const startEdit = (n) => {
    setEditingId(n.note_id);
    setEditHtml(n.content_html || (n.content ? `<p>${n.content.replace(/&/g, "&amp;").replace(/</g, "&lt;")}</p>` : ""));
    setEditMentions((n.mentions || []).map((m) => (typeof m === "string" ? { user_id: m, name: "" } : m)));
    setEditAttachments(n.attachments || []);
  };

  const saveEdit = async (noteId) => {
    const content = htmlToText(editHtml);
    if (!content && !editAttachments.length) return;
    try {
      const { data } = await api.patch(`/notes/${noteId}`, {
        content: content || "(attachment)", content_html: editHtml,
        attachments: editAttachments, mentions: editMentions.map((m) => m.user_id),
      });
      setNotes((prev) => prev.map((n) => (n.note_id === noteId ? { ...n, ...data } : n)));
      setEditingId(null);
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Could not save note");
    }
  };

  const toggleResolved = async (n) => {
    try {
      const { data } = await api.post(`/notes/${n.note_id}/resolve`, { resolved: !n.resolved });
      // If it no longer matches the resolved filter, drop it from view.
      if (!showResolved && data.resolved) {
        setNotes((prev) => prev.filter((x) => x.note_id !== n.note_id));
      } else {
        setNotes((prev) => prev.map((x) => (x.note_id === n.note_id ? data : x)));
      }
      refreshCounts();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Could not update note");
    }
  };

  const remove = async (noteId) => {
    if (!window.confirm("Delete this note? This cannot be undone.")) return;
    try {
      await api.delete(`/notes/${noteId}`);
      setNotes((prev) => prev.filter((n) => n.note_id !== noteId));
      refreshCounts();
      if (focus) notifyNotesChanged(focus.entityType, focus.entityId);
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Could not delete note");
    }
  };

  // Resolve toggle for a record note inside focus view (updates in place).
  const toggleResolvedFocus = async (n) => {
    try {
      const { data } = await api.post(`/notes/${n.note_id}/resolve`, { resolved: !n.resolved });
      setNotes((prev) => prev.map((x) => (x.note_id === n.note_id ? data : x)));
      if (focus) notifyNotesChanged(focus.entityType, focus.entityId);
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Could not update note");
    }
  };

  const unpin = async (n) => {
    try {
      await api.post(`/notes/${n.note_id}/pin`, { pinned: false });
      setNotes((prev) => prev.filter((x) => x.note_id !== n.note_id));
      refreshCounts();
      toast.success("Removed from sidebar");
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Could not unpin note");
    }
  };

  // In focus view we pin/unpin in place (don't remove from the record's list).
  const togglePinInPlace = async (n) => {
    try {
      const { data } = await api.post(`/notes/${n.note_id}/pin`, { pinned: !n.pinned });
      setNotes((prev) => prev.map((x) => (x.note_id === n.note_id ? data : x)));
      refreshCounts();
      toast.success(data.pinned ? "Pinned to the Notes sidebar" : "Unpinned");
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Could not update note");
    }
  };

  const openRecord = (n) => {
    const link = recordLink(n.entity_type, n.entity_id);
    if (link) { setOpen(false); navigate(link); }
  };

  const badge = counts.module_open;

  const panel = (
    <AnimatePresence>
      {open && (
        <>
          <motion.div
            data-testid="global-notes-overlay"
            initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
            onClick={() => setOpen(false)}
            className="fixed inset-0 z-[70] bg-black/50 backdrop-blur-sm"
          />
          <motion.aside
            data-testid="global-notes-panel"
            initial={{ x: "100%" }} animate={{ x: 0 }} exit={{ x: "100%" }}
            transition={{ type: "tween", duration: 0.22 }}
            className="fixed inset-y-0 right-0 z-[71] w-full max-w-md bg-zinc-950 border-l border-zinc-800 flex flex-col shadow-2xl"
          >
            <div className="h-16 flex items-center gap-2 px-5 border-b border-zinc-800 flex-shrink-0">
              <StickyNote className="h-4 w-4 text-indigo-400" />
              <div className="min-w-0">
                <p className="font-[Outfit] font-bold text-base leading-tight">Notes</p>
                <p className="text-[11px] text-zinc-500 truncate" data-testid="global-notes-context-label">
                  {focus ? (focus.label || "This record") : (showAll ? "All modules" : ctx.label)}
                </p>
              </div>
              <button data-testid="global-notes-close" onClick={() => setOpen(false)}
                className="ml-auto text-zinc-500 hover:text-zinc-100 p-1"><X className="h-5 w-5" /></button>
            </div>

            {focus ? (
              <div className="px-5 py-2.5 border-b border-zinc-800 flex-shrink-0 flex items-center gap-2">
                <span className="inline-flex items-center gap-1 text-[10px] font-medium px-2 py-0.5 rounded-full bg-amber-500/10 text-amber-300 border border-amber-500/25 truncate max-w-[220px]">
                  <Pin className="h-2.5 w-2.5" /> {focus.label || "Record"}
                </span>
                <button data-testid="global-notes-exit-focus" onClick={() => setFocus(null)}
                  className="ml-auto text-[11px] text-indigo-300 hover:text-indigo-200">
                  View module notes
                </button>
              </div>
            ) : (
              <div className="px-5 py-3 border-b border-zinc-800 flex items-center gap-5 flex-shrink-0">
                <label className="flex items-center gap-2 text-xs text-zinc-400 cursor-pointer">
                  <Switch data-testid="global-notes-show-all" checked={showAll} onCheckedChange={setShowAll} />
                  <span className="flex items-center gap-1"><Globe className="h-3.5 w-3.5" /> Show all notes</span>
                </label>
                <label className="flex items-center gap-2 text-xs text-zinc-400 cursor-pointer">
                  <Switch data-testid="global-notes-show-resolved" checked={showResolved} onCheckedChange={setShowResolved} />
                  <span>Show resolved</span>
                </label>
              </div>
            )}

            {/* Composer */}
            <div className="px-5 py-3 border-b border-zinc-800 flex-shrink-0">
              <div className="flex gap-2 items-end">
                <div className="flex-1 min-w-0">
                  <RichNoteEditor
                    testId="global-notes-input"
                    html={html}
                    onChange={setHtml}
                    mentions={mentions}
                    onMentionsChange={setMentions}
                    attachments={attachments}
                    onAttachmentsChange={setAttachments}
                    module={composerModule}
                    onSubmitShortcut={submit}
                    placeholder={`Add a note for ${focus ? (focus.label || "this record") : ctx.label}… (@ to mention, paste a screenshot)`}
                  />
                </div>
                <button
                  type="button"
                  data-testid="global-notes-submit"
                  onClick={submit}
                  disabled={submitting || (!htmlToText(html) && !attachments.length)}
                  title="Add note"
                  className="h-9 w-9 flex-shrink-0 rounded-lg bg-indigo-600 hover:bg-indigo-500 disabled:opacity-40 disabled:cursor-not-allowed flex items-center justify-center text-white transition-colors"
                >
                  {submitting ? <Loader2 className="h-4 w-4 animate-spin" /> : <Send className="h-4 w-4" />}
                </button>
              </div>
              <p className="mt-1.5 text-[10px] text-zinc-600">
                Tagged to <span className="text-zinc-400 font-medium">{focus ? (focus.label || "this record") : ctx.label}</span>
                {mentions.length > 0 && <span> · notifying {mentions.length}</span>}
              </p>
            </div>

            {/* List */}
            <div className="flex-1 overflow-y-auto px-5 py-4 space-y-3">
              {loading ? (
                <div className="py-6 flex justify-center"><Loader2 className="h-5 w-5 animate-spin text-indigo-400" /></div>
              ) : notes.length === 0 ? (
                <p className="text-[11px] text-zinc-600 py-6 text-center border border-dashed border-zinc-800 rounded-lg flex items-center justify-center gap-1.5">
                  <MessageSquare className="h-3.5 w-3.5" /> No notes here yet — add the first.
                </p>
              ) : (
                notes.map((n) => (
                  <div key={n.note_id} data-testid={`global-note-${n.note_id}`}
                    className={`rounded-lg border p-3 transition-colors ${n.resolved ? "border-zinc-800/60 bg-zinc-900/40 opacity-70" : "border-zinc-800 bg-zinc-900"}`}>
                    <div className="flex items-center gap-2 flex-wrap mb-1.5">
                      <UserInitialsChip name={n.user_name} testId={`global-note-avatar-${n.note_id}`} />
                      <span className="text-xs font-medium text-zinc-200">{n.user_name}</span>
                      <span className="text-[11px] text-zinc-600">{timeLabel(n.created_at)}{n.edited ? " · edited" : ""}</span>
                      <span className="flex-1" />
                      {focus && n.can_resolve && (
                        <button type="button" onClick={() => toggleResolvedFocus(n)}
                          data-testid={`global-note-resolve-${n.note_id}`}
                          title={n.resolved ? "Mark as open" : "Mark as resolved"}
                          className={`p-0.5 ${n.resolved ? "text-emerald-400 hover:text-emerald-300" : "text-zinc-500 hover:text-emerald-400"}`}>
                          {n.resolved ? <CheckCircle2 className="h-4 w-4" /> : <Circle className="h-4 w-4" />}
                        </button>
                      )}
                      {focus && n.can_pin && (
                        <button type="button" onClick={() => togglePinInPlace(n)}
                          data-testid={`global-note-pin-${n.note_id}`}
                          title={n.pinned ? "Unpin from module sidebar" : "Pin to module sidebar"}
                          className={`p-0.5 ${n.pinned ? "text-amber-400 hover:text-amber-300" : "text-zinc-500 hover:text-amber-400"}`}>
                          {n.pinned ? <Pin className="h-3.5 w-3.5" /> : <PinOff className="h-3.5 w-3.5" />}
                        </button>
                      )}
                      {!focus && !n.is_record && n.can_resolve && (
                        <button type="button" onClick={() => toggleResolved(n)}
                          data-testid={`global-note-resolve-${n.note_id}`}
                          title={n.resolved ? "Mark as open" : "Mark as resolved"}
                          className={`p-0.5 ${n.resolved ? "text-emerald-400 hover:text-emerald-300" : "text-zinc-500 hover:text-emerald-400"}`}>
                          {n.resolved ? <CheckCircle2 className="h-4 w-4" /> : <Circle className="h-4 w-4" />}
                        </button>
                      )}
                      {!focus && n.is_record && n.can_pin && (
                        <button type="button" onClick={() => unpin(n)}
                          data-testid={`global-note-unpin-${n.note_id}`}
                          title="Remove from sidebar (unpin)"
                          className="text-zinc-500 hover:text-amber-400 p-0.5">
                          <PinOff className="h-3.5 w-3.5" />
                        </button>
                      )}
                      {n.can_edit && editingId !== n.note_id && (
                        <button type="button" onClick={() => startEdit(n)}
                          title="Edit" className="text-zinc-500 hover:text-indigo-300 p-0.5" data-testid={`global-note-edit-${n.note_id}`}>
                          <Pencil className="h-3.5 w-3.5" />
                        </button>
                      )}
                      {n.can_delete && editingId !== n.note_id && (
                        <button type="button" onClick={() => remove(n.note_id)}
                          title="Delete" className="text-zinc-500 hover:text-red-400 p-0.5" data-testid={`global-note-delete-${n.note_id}`}>
                          <Trash2 className="h-3.5 w-3.5" />
                        </button>
                      )}
                    </div>

                    {(showAll || n.resolved || n.is_record) && (
                      <div className="mb-1.5 flex items-center gap-1.5 flex-wrap">
                        <span className="inline-flex items-center text-[10px] font-medium px-2 py-0.5 rounded-full bg-indigo-600/15 text-indigo-300 border border-indigo-600/25">
                          {n.module_label}
                        </span>
                        {n.is_record && (
                          <button type="button" onClick={() => openRecord(n)}
                            data-testid={`global-note-record-${n.note_id}`}
                            title="Open this record"
                            className="inline-flex items-center gap-1 text-[10px] font-medium px-2 py-0.5 rounded-full bg-amber-500/10 text-amber-300 border border-amber-500/25 hover:bg-amber-500/20 transition-colors max-w-[180px]">
                            <Pin className="h-2.5 w-2.5" />
                            <span className="truncate">{n.record_label || "Record"}</span>
                            <ExternalLink className="h-2.5 w-2.5" />
                          </button>
                        )}
                        {n.resolved && (
                          <span className="inline-flex items-center gap-1 text-[10px] font-medium px-2 py-0.5 rounded-full bg-emerald-500/10 text-emerald-300 border border-emerald-500/20">
                            <Check className="h-2.5 w-2.5" /> Resolved{n.resolved_by ? ` · ${n.resolved_by}` : ""}
                          </span>
                        )}
                      </div>
                    )}

                    {editingId === n.note_id ? (
                      <div className="space-y-1.5">
                        <RichNoteEditor
                          testId={`global-note-edit-input-${n.note_id}`}
                          html={editHtml}
                          onChange={setEditHtml}
                          mentions={editMentions}
                          onMentionsChange={setEditMentions}
                          attachments={editAttachments}
                          onAttachmentsChange={setEditAttachments}
                          module={composerModule}
                          onSubmitShortcut={() => saveEdit(n.note_id)}
                          autoFocus
                          placeholder="Edit note… (@ to mention, paste a screenshot)"
                        />
                        <div className="flex gap-1.5">
                          <button type="button" onClick={() => saveEdit(n.note_id)} data-testid={`global-note-save-${n.note_id}`}
                            className="inline-flex items-center gap-1 text-[11px] px-2 py-1 rounded bg-indigo-600 hover:bg-indigo-500 text-white">
                            <Check className="h-3 w-3" /> Save
                          </button>
                          <button type="button" onClick={() => setEditingId(null)}
                            className="inline-flex items-center gap-1 text-[11px] px-2 py-1 rounded bg-zinc-800 hover:bg-zinc-700 text-zinc-300">
                            <X className="h-3 w-3" /> Cancel
                          </button>
                        </div>
                      </div>
                    ) : (
                      <div className={n.resolved ? "opacity-60 line-through decoration-zinc-700" : ""}>
                        <NoteBody note={n} />
                        <NoteAttachments attachments={n.attachments} />
                      </div>
                    )}
                    <div className="mt-1.5 flex items-center gap-2 flex-wrap">
                      <DueDateControl note={n} onUpdated={(data) => setNotes((prev) => prev.map((x) => (x.note_id === n.note_id ? { ...x, ...data } : x)))} />
                      <AssignControl note={n} onUpdated={(data) => setNotes((prev) => prev.map((x) => (x.note_id === n.note_id ? { ...x, ...data } : x)))} />
                    </div>
                  </div>
                ))
              )}
            </div>
          </motion.aside>
        </>
      )}
    </AnimatePresence>
  );

  return (
    <>
      <button
        data-testid="global-notes-trigger"
        onClick={() => setOpen(true)}
        title="Notes"
        className="relative flex items-center justify-center h-[34px] w-[34px] text-zinc-400 hover:text-indigo-300 border border-zinc-800 hover:border-zinc-700 rounded-lg transition-colors"
      >
        <StickyNote className="h-4 w-4" />
        {badge > 0 && (
          <span data-testid="global-notes-badge"
            className="absolute -top-1.5 -right-1.5 min-w-[16px] h-[16px] px-1 rounded-full bg-indigo-600 text-white text-[10px] font-bold flex items-center justify-center">
            {badge > 9 ? "9+" : badge}
          </span>
        )}
      </button>
      {typeof document !== "undefined" ? createPortal(panel, document.body) : panel}
    </>
  );
}
